import { useState, useCallback, useMemo } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
  type Edge,
} from '@xyflow/react';
import { makeStyles, tokens, mergeClasses } from '@coreai/fluentui-react';
import { AddFilled, CheckmarkCircleFilled, DismissCircleFilled } from '@fluentui/react-icons';
import { useIsHorizontalLayout } from '@/hooks/useLayoutOrientation';
import { EdgePillButton } from './EdgePillButton';
import {
  type AgentVariant,
  getAgentBadgeTokens,
} from '../helpers/agentVariant';

export type DottedEdgeData = {
  label?: string;
  onAddClick?: (sourceId: string, targetId: string) => void;
  /** When set, color the dotted stroke to match the source agent's identity. */
  agentVariant?: AgentVariant;
};

export type DottedEdgeType = Edge<DottedEdgeData, 'dotted'>;

const useStyles = makeStyles({
  branchLabel: {
    position: 'absolute',
    pointerEvents: 'none',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '1',
    display: 'flex',
    alignItems: 'center',
    // Sub-token intentional spacing: no Fluent token matches 3px
    gap: '3px',
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalSNudge}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    // Fade in
    animationName: {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    animationDuration: tokens.durationNormal,
    animationTimingFunction: tokens.curveDecelerateMax,
    animationFillMode: 'backwards',
    animationDelay: tokens.durationFast,
    '@media (prefers-reduced-motion: reduce)': {
      animationDuration: '0.01ms',
      animationDelay: '0ms',
    },
  },
  trueLabel: {
    color: tokens.colorPaletteGreenForeground1,
  },
  falseLabel: {
    color: tokens.colorPaletteRedForeground1,
  },
  icon: {
    fontSize: tokens.fontSizeBase100,
    flexShrink: 0,
  },
  edgeButtonWrapper: {
    position: 'absolute',
    pointerEvents: 'all',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  interactionPath: {
    fill: 'none',
    stroke: 'transparent',
    strokeWidth: 20,
    cursor: 'pointer',
  },
  addIcon: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
});

/**
 * Dotted edge component for parent-child relationships.
 * When used for If condition branches (sourceHandle = "true"/"false"),
 * renders a colored "True"/"False" label at the edge midpoint.
 */
export const DottedEdge = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  sourceHandleId,
  source,
  target,
  data,
  style,
  markerEnd,
}: EdgeProps<DottedEdgeType>) => {
  const styles = useStyles();
  const isHorizontal = useIsHorizontalLayout();
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Determine if this is a condition branch edge.
  // The "True"/"False" chip on the edge midpoint is only useful in horizontal
  // mode where the diamond node has no inline branch labels (handles are at
  // top/bottom corners). In vertical mode the FloatingIfNode renders True /
  // After / False labels right below the diamond, so this chip is redundant
  // visual noise.
  const isTrueBranch = sourceHandleId === 'true';
  const isFalseBranch = sourceHandleId === 'false';
  const isConditionBranch = isTrueBranch || isFalseBranch;
  const showConditionChip = isConditionBranch && isHorizontal;
  const isLoopBranch = sourceHandleId === 'loop';

  const onAddClick = data?.onAddClick;
  const showAddButton =
    (isConditionBranch || isLoopBranch) &&
    typeof onAddClick === 'function';

  const handleAddAction = useCallback(() => {
    onAddClick?.(source, target);
  }, [onAddClick, source, target]);

  // Use branch-specific color for condition fork edges; agent-tool edges use
  // the agent's identity color (teal=native, purple=Foundry, green=Coding) so
  // the dotted line visually pairs with the hexagon it leaves.
  let strokeColor = tokens.colorNeutralStroke2;
  if (isTrueBranch) {
    strokeColor = tokens.colorPaletteGreenBorder2;
  } else if (isFalseBranch) {
    strokeColor = tokens.colorPaletteRedBorder2;
  } else if (data?.agentVariant) {
    strokeColor = getAgentBadgeTokens(data.agentVariant).foreground;
  }

  const edgeStyle: React.CSSProperties = {
    ...style,
    strokeWidth: 2,
    stroke: strokeColor,
    strokeDasharray: '4,4',
  };

  // Place the + button at the bezier's actual arc-length midpoint (the
  // visually-centered point on the curve), not labelX/labelY — which is just
  // the parametric midpoint and skews toward an end on heavily-curved edges.
  // SVG's getPointAtLength gives us the true arc midpoint cheaply; we memo
  // on edgePath so we only do the DOM measurement when the path changes.
  const arcMidpoint = useMemo<{ x: number; y: number } | null>(() => {
    if (typeof document === 'undefined') return null;
    try {
      const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      pathEl.setAttribute('d', edgePath);
      const totalLength = pathEl.getTotalLength();
      if (!totalLength || !Number.isFinite(totalLength)) return null;
      const point = pathEl.getPointAtLength(totalLength / 2);
      return { x: point.x, y: point.y };
    } catch {
      return null;
    }
  }, [edgePath]);

  // Use the arc midpoint when available; otherwise fall back to labelX/labelY
  // (e.g., in jsdom where SVGPathElement.getTotalLength is not implemented).
  const buttonX = arcMidpoint?.x ?? labelX;
  const buttonY = arcMidpoint?.y ?? labelY;

  // Place the True / False pill at t=0.4 along the arc length, pushed
  // ~26px perpendicular to the curve on the outward side. This separates
  // it diagonally from the "+" button (which sits at the arc midpoint,
  // t=0.5) so the two never visually collide on hover, and it clears the
  // node title/subtitle that sits below the source diamond.
  const chipPosition = useMemo<{ x: number; y: number }>(() => {
    // Fallback (jsdom, very short edges, or getTotalLength failures): keep
    // the previous linear-interpolation behavior so unit tests remain stable.
    const fallback = {
      x: sourceX + (buttonX - sourceX) * (showAddButton ? 0.35 : 1),
      y: sourceY + (buttonY - sourceY) * (showAddButton ? 0.35 : 1),
    };
    if (typeof document === 'undefined') return fallback;
    try {
      const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      pathEl.setAttribute('d', edgePath);
      const total = pathEl.getTotalLength();
      if (!total || !Number.isFinite(total)) return fallback;

      const CHIP_T = 0.4;
      const PERP_OFFSET = 26;
      const center = pathEl.getPointAtLength(total * CHIP_T);

      // Tangent via central finite difference; epsilon = 1% of arc length
      // (clamped) gives a stable direction even on short edges.
      const eps = Math.max(0.5, total * 0.01);
      const before = pathEl.getPointAtLength(Math.max(0, total * CHIP_T - eps));
      const after = pathEl.getPointAtLength(Math.min(total, total * CHIP_T + eps));
      const tx = after.x - before.x;
      const ty = after.y - before.y;
      const len = Math.hypot(tx, ty);
      if (len < 0.0001) return fallback;

      // Normal candidates: (-ty/len, tx/len) and its negation. Pick the one
      // pointing AWAY from the other branch (True pushes toward smaller y,
      // False toward larger y, relative to source). For loop branches we
      // bias the same direction as True (curve goes up over the source).
      let nx = -ty / len;
      let ny = tx / len;
      const desiredYSign = isTrueBranch || isLoopBranch ? -1 : 1;
      if (Math.sign(ny) !== desiredYSign) {
        nx = -nx;
        ny = -ny;
      }
      return {
        x: center.x + nx * PERP_OFFSET,
        y: center.y + ny * PERP_OFFSET,
      };
    } catch {
      return fallback;
    }
  }, [edgePath, sourceX, sourceY, buttonX, buttonY, isTrueBranch, isLoopBranch, showAddButton]);

  const chipX = chipPosition.x;
  const chipY = chipPosition.y;

  const branchLabel = isTrueBranch ? 'True' : 'False';
  let addAriaLabel = 'Add action';
  if (isTrueBranch) {
    addAriaLabel = 'Add action at start of True branch';
  } else if (isFalseBranch) {
    addAriaLabel = 'Add action at start of False branch';
  } else if (isLoopBranch) {
    addAriaLabel = 'Add action at start of loop body';
  }

  return (
    <>
      <BaseEdge
        path={edgePath}
        {...(markerEnd ? { markerEnd } : {})}
        style={edgeStyle}
      />
      {showAddButton && (
        <path
          d={edgePath}
          className={styles.interactionPath}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      )}
      {(showConditionChip || showAddButton) && (
        <EdgeLabelRenderer>
          {showConditionChip && (
            <div
              className={mergeClasses(
                styles.branchLabel,
                isTrueBranch ? styles.trueLabel : styles.falseLabel
              )}
              style={{
                transform: `translate(-50%, -50%) translate(${chipX}px, ${chipY}px)`,
              }}
            >
              {isTrueBranch ? (
                <CheckmarkCircleFilled className={styles.icon} aria-hidden="true" />
              ) : (
                <DismissCircleFilled className={styles.icon} aria-hidden="true" />
              )}
              {branchLabel}
            </div>
          )}
          {showAddButton && (
            <div
              className={styles.edgeButtonWrapper}
              style={{
                transform: `translate(-50%, -50%) translate(${buttonX}px, ${buttonY}px)`,
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <EdgePillButton
                icon={<AddFilled className={styles.addIcon} />}
                tooltip="Add an action"
                ariaLabel={addAriaLabel}
                isRevealed={isHovered}
                onActivate={handleAddAction}
              />
            </div>
          )}
        </EdgeLabelRenderer>
      )}
    </>
  );
};
